import { t, Trans } from "@lingui/macro";
import { CaretDownIcon, CheckIcon } from "@phosphor-icons/react";
import { useState } from "react";

import type { PricingCycle } from "./pricing-prices";
import {
  trialistPurchaseTerms,
  type TrialTermsMode,
  unknownAccountPurchaseTerms,
} from "./pricing-purchase-terms";

type CtaVariant = "primary" | "secondary" | "ghost";

export type Tier = {
  id: "free" | "pro" | "ultimate";
  name: string;
  tagline: string;
  // Display amounts. `yearly` is the per-month-equivalent shown on the card;
  // `yearlyTotal` is the annual figure used in the "billed $X/year" subcopy.
  // `weekly` is present only for PRO under V2.
  monthly: number;
  yearly: number;
  yearlyTotal: number;
  weekly?: number;
  // Annual "save N%" badge value.
  saveYearlyPct?: number;
  cta: string;
  ctaVariant: CtaVariant;
  ctaHref: string;
  note: string;
  popular?: boolean;
  bestValue?: boolean;
  features: readonly string[];
  coming?: string;
};

type Cycle = PricingCycle;
type PriceTagProps = { amount: number; cycle: Cycle };
const PriceTag = ({ amount, cycle }: PriceTagProps) => {
  if (amount === 0) {
    return (
      <div className="flex flex-wrap items-baseline gap-1">
        <span className="text-[36px] font-semibold tracking-tight text-foreground sm:text-[44px]">
          $0
        </span>
        <span className="text-[14px] text-muted-foreground">/month</span>
      </div>
    );
  }
  const whole = Math.floor(amount);
  const cents = (amount % 1).toFixed(2).slice(2);
  // Spec 036 — per-unit suffix. Weekly is a flat /week; yearly shows the
  // per-month-equivalent (t`mo, billed yearly`).
  const CYCLE_SUFFIX: Record<Cycle, string> = {
    weekly: t`week`,
    monthly: t`month`,
    yearly: t`mo, billed yearly`,
  };
  const suffix = CYCLE_SUFFIX[cycle];
  return (
    <div className="flex flex-wrap items-baseline gap-1">
      <span className="text-[36px] font-semibold tracking-tight text-foreground sm:text-[44px]">
        ${whole}
      </span>
      <span className="text-[24px] font-semibold text-foreground">.{cents}</span>
      <span className="text-[14px] text-muted-foreground">/{suffix}</span>
    </div>
  );
};

type TierCardProps = {
  tier: Tier;
  cycle: Cycle;
  onCheckout?: (tierId: Tier["id"]) => void;
  onOpenCheckout?: (checkoutUrl: string, tierId: "pro" | "ultimate", cycle: Cycle) => void;
  /** True while this tier's `POST /billing/checkouts` request is in flight. */
  isCheckoutPending?: boolean;
  /** Terms line under the paid CTA: priced for a known trialist, generic while unknown. */
  trialTerms?: TrialTermsMode | null;
  className?: string;
};
export const TierCard = ({
  tier,
  cycle,
  onCheckout,
  onOpenCheckout,
  isCheckoutPending,
  trialTerms,
  className = "",
}: TierCardProps) => {
  // Spec 036 — weekly only exists for PRO; tiers without it fall back to monthly
  // so a card never renders blank when the global cycle is "weekly".
  const effectiveCycle: Cycle = cycle === "weekly" && tier.weekly === undefined ? "monthly" : cycle;
  const AMOUNT_BY_CYCLE: Record<Cycle, number> = {
    weekly: tier.weekly ?? tier.monthly,
    monthly: tier.monthly,
    yearly: tier.yearly,
  };
  const amount = AMOUNT_BY_CYCLE[effectiveCycle];
  // The figure the checkout charges today: the card shows yearly as a
  // per-month equivalent, but the buyer is billed the annual total.
  const amountDueToday = effectiveCycle === "yearly" ? tier.yearlyTotal : amount;
  const isPopular = !!tier.popular;
  const isBest = !!tier.bestValue;
  const [expanded, setExpanded] = useState(false);
  const featureCount = tier.features.length + Number(Boolean(tier.coming));
  const savePct = tier.saveYearlyPct;

  let ctaClasses = ["border", "border-border", "text-foreground", "hover:bg-muted"].join(" ");
  if (tier.ctaVariant === "primary") {
    ctaClasses = [
      "bg-primary",
      "text-primary-foreground",
      "shadow-[0_6px_20px_-6px_hsl(var(--primary)/0.45)]",
      "hover:opacity-90",
    ].join(" ");
  } else if (tier.ctaVariant === "secondary") {
    ctaClasses = [
      "bg-primary-soft",
      "text-primary-soft-foreground",
      "border",
      "border-primary-soft-border",
      "hover:opacity-90",
    ].join(" ");
  }

  return (
    <div
      data-testid="pricing-tier"
      className={
        "relative flex flex-col rounded-xl border bg-surface p-5 shadow-sm transition-all duration-150 ease-out hover:shadow-md sm:p-8 sm:hover:-translate-y-[1px] " +
        (isPopular ? "border-primary md:scale-[1.02]" : "border-border") +
        " " +
        className
      }
    >
      {isPopular && (
        <span className="absolute -top-3 left-5 rounded-full bg-primary-soft px-3 py-1 text-xs font-medium text-primary-soft-foreground sm:left-7">
          <Trans>Most popular</Trans>
        </span>
      )}
      {isBest && (
        <span className="absolute -top-3 left-5 rounded-full border border-border bg-surface px-3 py-1 text-xs font-medium text-muted-foreground sm:left-7">
          <Trans>Best value</Trans>
        </span>
      )}
      <div className="flex items-baseline justify-between">
        <h3 className="text-[20px] font-semibold tracking-tight text-foreground">{tier.name}</h3>
        {/* Annual savings badge — reads the pinned "save N%". */}
        {tier.id !== "free" && effectiveCycle === "yearly" && (
          <span className="rounded-full bg-emerald-500/10 px-2 py-0.5 text-xs font-medium text-emerald-700 dark:text-emerald-400">
            {savePct === undefined ? t`2 mo free` : t`Save ${savePct}%`}
          </span>
        )}
      </div>
      <p className="mt-1 text-[13.5px] text-muted-foreground">{tier.tagline}</p>
      <div className="mt-6">
        <PriceTag amount={amount} cycle={effectiveCycle} />
        {effectiveCycle !== cycle && (
          <p className="mt-1.5 text-[12.5px] text-muted-foreground">
            <Trans>Billed monthly. Weekly billing is Pro only.</Trans>
          </p>
        )}
        <p className="mt-2 text-[13px] text-muted-foreground">{tier.note}</p>
      </div>
      {/* Paid MONTHLY tiers (and the free tier's registration link) open as a
          real anchor whose href stays a plain checkout URL, so middle-click
          and a lemon.js failure both fall back to the hosted checkout — safe
          for monthly, which is what LS's hosted page defaults to anyway.
          Weekly/yearly render a plain button with NO href instead (Codex
          review, 2026-09-21 — BLOCKER A): that static URL pre-selects
          Monthly on LS's page, so there is no safe href to reach directly. */}
      {tier.id === "free" || effectiveCycle === "monthly" ? (
        <a
          href={tier.ctaHref}
          data-testid={`pricing-cta-${tier.id}`}
          aria-disabled={isCheckoutPending}
          className={
            "mt-6 inline-flex h-11 w-full items-center justify-center rounded-xl text-[14px] font-medium transition duration-150 ease-out hover:shadow-sm sm:hover:-translate-y-[1px] " +
            ctaClasses
          }
          onClick={(event) => {
            if (tier.id !== "free" && onOpenCheckout) {
              // Read the URL at click time so a cycle toggle can't open a stale price.
              event.preventDefault();
              onOpenCheckout(tier.ctaHref, tier.id, effectiveCycle);
            }
            onCheckout?.(tier.id);
          }}
        >
          {tier.cta}
        </a>
      ) : (
        <button
          type="button"
          data-testid={`pricing-cta-${tier.id}`}
          disabled={isCheckoutPending}
          className={
            "mt-6 inline-flex h-11 w-full items-center justify-center rounded-xl text-[14px] font-medium transition duration-150 ease-out hover:shadow-sm sm:hover:-translate-y-[1px] " +
            ctaClasses
          }
          onClick={() => {
            if (onOpenCheckout)
              onOpenCheckout(tier.ctaHref, tier.id as "pro" | "ultimate", effectiveCycle);
            onCheckout?.(tier.id);
          }}
        >
          {tier.cta}
        </button>
      )}
      {trialTerms && tier.id !== "free" && (
        // What the checkout above charges, for a user who still has free trial
        // days: the purchase is charged today and replaces the trial. While the
        // account is unknown (lookup pending/failed) the wording is conditional.
        <p
          data-testid={`trial-terms-${tier.id}`}
          className="mt-3 text-[12px] text-muted-foreground"
        >
          {trialTerms === "trialist"
            ? trialistPurchaseTerms(`$${amountDueToday.toFixed(2)}`, effectiveCycle)
            : unknownAccountPurchaseTerms()}
        </p>
      )}
      <div className={`mt-6 h-px bg-border ${tier.id === "free" ? "max-md:hidden" : ""}`} />
      <ul
        className={`mt-5 space-y-2.5 text-[13.5px] sm:space-y-3 ${tier.id === "free" ? "max-md:hidden" : ""}`}
      >
        {tier.features.map((f, index) => (
          <li
            key={f}
            className={`${index >= 5 && !expanded ? "hidden md:flex" : "flex"} items-start gap-2.5`}
          >
            <span className="mt-0.5 grid size-4 shrink-0 place-items-center rounded-full bg-primary/10 text-primary">
              <CheckIcon size={11} weight="bold" />
            </span>
            <span className="text-foreground/90">{f}</span>
          </li>
        ))}
        {tier.coming && (
          <li
            className={`${featureCount > 5 && !expanded ? "hidden md:flex" : "flex"} items-start gap-2.5 text-muted-foreground`}
          >
            <span className="mt-0.5 grid size-4 shrink-0 place-items-center rounded-full border border-border">
              <span className="size-1.5 rounded-full bg-muted-foreground/50" />
            </span>
            <span className="flex flex-col">
              {tier.coming}
              <span className="mt-1 inline-flex w-fit whitespace-nowrap rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium">
                <Trans>Coming soon</Trans>
              </span>
            </span>
          </li>
        )}
      </ul>
      {featureCount > 5 && tier.id !== "free" && (
        <button
          type="button"
          aria-expanded={expanded}
          className="flex min-h-11 w-full flex-row items-center justify-center gap-1 rounded-lg border border-border px-3.5 py-2 text-sm font-medium text-primary md:hidden"
          onClick={() => {
            setExpanded(!expanded);
          }}
        >
          {expanded ? t`Show fewer` : t`See all ${featureCount} features`}
          <CaretDownIcon size={16} className={expanded ? "rotate-180" : ""} />
        </button>
      )}
    </div>
  );
};
